import React, {
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isPlayerSupported } from 'amazon-ivs-player';

import { MediaItem } from '../../../../stores/reducers/media';
import { GreenroomState } from '../../../../stores/types/types';
import {
	durationString,
	getChannelLogoFromMeeting,
	getVideoRect,
} from '../../../../utils/utils';
import { setModalOpen, setModalState } from '../../../../stores/actions/modal';
import Icon, { COLORS, ICONS } from '../../../general-components/icon';
import MediaVideo from '../../../general-components/media-video';
import TrimTimeline from './trim-timeline';
import {
	getVideoElDuration,
	getVideoElElapsed,
	setVideoElElapsed,
	setVideoElPlaying,
} from '../../../../utils/video-element';
import { showAlert } from '../../../general-components/alert-service';
import * as SessionActions from '../../../../stores/actions/session';
import { MEDIA_TABS } from '../../../../stores/reducers/session';
import * as Alerts from '../../../general-components/alert-service';
import RenameDialog from './rename-dialog';
import { TrimSaveAs, TrimSaveOver } from '../../../../connection/trim';
import { MODAL_TEMPLATES } from '../../../../stores/reducers/modal';
import useMedia from '../../../../hooks/use-media';
import { checkFileName, getVideoDuration } from '../../../../utils/media';
import { ResourcePoolContext } from '../../../../hooks/use-resource-pool';
import TellyChannelLogo from '../../../../images/app-store-icon.png';
import HeaderLeftControls from '../../../header-components/header-left-controls';
import { MEDIA_TYPES } from '../../../../types/constants';
import { TABS } from '../../../../stores/types/tabs';
import { setActiveSubTab } from '../../../../stores/actions/tabs';

interface Props {
	mediaId: number;
	editorPage: string;
}

export const VIDEO_EDITOR_PAGES = {
	SELECT_MEDIA_PAGE: 'select_media_page',
	TRIM_EDITOR: 'trim_editor',
};

const SKIP_SECONDS = 10;

export interface TrimTime {
	start: number,
	end: number,
}

function getTimecodesFromSeconds(startSeconds: number, endSeconds: number) {
	// assume all videos are at 30fps
	const startFrames = Math.ceil((startSeconds - Math.floor(startSeconds)) * 30);
	const startFramesString = `${startFrames < 10 ? '0' : ''}${startFrames}`;
	const startTimecode = `${durationString(startSeconds, true)}:${startFramesString}`;

	const endFrames = Math.floor((endSeconds - Math.floor(endSeconds)) * 30);
	const endFramesString = `${endFrames < 10 ? '0' : ''}${endFrames}`;
	const endTimecode = `${durationString(endSeconds, true)}:${endFramesString}`;

	return [startTimecode, endTimecode];
}

export default function VideoEditor(props: Props) {
	const {
		mediaId,
		editorPage,
	} = props;

	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const meeting = useSelector((state: GreenroomState) => state.SessionReducer.meeting);
	const media = useSelector((state: GreenroomState) => state.MediaReducer.media);

	const dispatch = useDispatch();

	const videoElRef = useRef<HTMLVideoElement | null>(null);
	const videoWrapperRef = useRef<HTMLDivElement | null>(null);

	const [currentEditorPage] = useState<string>(editorPage);
	const [currentMedia, setCurrentMedia] = useState<MediaItem | null>(null);
	const [currentMediaPage] = useState<number>(1);
	const [videoWidthHeight, setVideoWidthHeight] = useState({
		height: '0',
		width: '0',
		top: '0px',
		left: '0px',
		bottom: '0px',
		right: '0px',
	});
	const [isPlaying, setIsPlaying] = useState<boolean>(false);
	const [elapsed, setElapsed] = useState<number>(0);
	const [duration, setDuration] = useState<number>(0);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [videoTimer, setVideoTimer] = useState<number>(0);
	const [timerRunning, setTimerRunning] = useState<boolean>(false);
	const [trimTimes, setTrimTimes] = useState<TrimTime>({ start: 0, end: duration });
	const [renameOpen, setRenameOpen] = useState(false);

	const {
		canTrimMediaItem,
	} = useMedia();

	const resourcePool = useContext(ResourcePoolContext);

	const resizeObserver = new ResizeObserver((entries) => {
		const wrapWidth = entries[0].contentRect.width;
		const wrapHeight = entries[0].contentRect.height;

		const styleObject = {
			...getVideoRect(wrapWidth, wrapHeight),
			right: 'auto',
			bottom: 'auto',
		};

		setVideoWidthHeight(styleObject);
	});

	const currentPageUrl = useMemo(
		() => (currentMedia ? currentMedia.media_data.image_array[currentMediaPage - 1] : ''),
		[currentMedia, currentMediaPage],
	);

	const posterImage = useMemo((): string => currentMedia?.thumbnail_url ?? '', [currentMedia]);

	const isHlsVideo = useMemo((): boolean => {
		if (currentPageUrl) {
			return currentPageUrl.endsWith('.m3u8') && isPlayerSupported;
		}

		return false;
	}, [currentPageUrl]);

	useEffect(() => {
		let videoTimerInterval: NodeJS.Timeout | null = null;

		if (timerRunning) {
			videoTimerInterval = setInterval(() => {
				setVideoTimer((time) => time + 1);
			}, 1000);
		}

		return () => {
			if (videoTimerInterval) {
				clearInterval(videoTimerInterval);
			}
		};
	}, [timerRunning]);

	const setVideoEditorElElapsed = (value: number) => {
		const isSet = setVideoElElapsed(videoElRef.current, value);

		if (isSet) {
			setElapsed(value);
			setVideoTimer(value);
		}
	};

	// Click handlers
	const handleSkipBack = () => {
		setVideoEditorElElapsed(trimTimes.start);
	};

	const handleRewind = () => {
		let setTimeTo = elapsed > SKIP_SECONDS
			? elapsed - SKIP_SECONDS
			: 0;

		if (setTimeTo < trimTimes.start) {
			setTimeTo = trimTimes.start;
		}

		setVideoEditorElElapsed(setTimeTo);
	};

	const handlePlay = () => {
		if (!isPlaying) {
			const currentTime = Math.floor(getVideoElElapsed(videoElRef.current));

			if (videoElRef.current && currentTime >= (trimTimes.end - 0.99)) {
				setVideoEditorElElapsed(trimTimes.start);

				setIsPlaying(true);
				return null;
			}

			setVideoTimer(elapsed);
		}

		setIsPlaying((currentlyPlaying) => !currentlyPlaying);
		return null;
	};

	const handleFastForward = () => {
		const proposedTime = elapsed + SKIP_SECONDS;
		let setTimeTo = proposedTime < duration
			? proposedTime
			: duration;

		if (setTimeTo > trimTimes.end) {
			setTimeTo = trimTimes.end;
		}

		setVideoEditorElElapsed(setTimeTo);
	};

	const handleSkipForward = () => {
		setVideoEditorElElapsed(trimTimes.end);
	};

	useEffect(() => {
		setVideoElPlaying(videoElRef.current, !!isPlaying);
		setTimerRunning(isPlaying);
	}, [isPlaying]);

	useEffect(() => {
		if (videoWrapperRef.current) resizeObserver.observe(videoWrapperRef.current);

		return () => {
			if (videoWrapperRef.current) resizeObserver.disconnect();
		};
	}, [currentPageUrl]);

	useEffect(() => {
		const mediaMatch = media.find((item: MediaItem) => item.id === mediaId);
		if (mediaMatch) {
			setCurrentMedia(mediaMatch);
		}
	}, [mediaId, media]);

	const closeModal = () => {
		dispatch(setModalOpen(false));
	};

	const onLoadedData = async (): Promise<void> => {
		let vidDuration = getVideoElDuration(
			videoElRef.current,
		);

		// Measure time the same way we do on canvas
		if (isHlsVideo && currentPageUrl && resourcePool) {
			const videoElm = document.createElement('video');

			videoElm.preload = 'metadata';
			videoElm.crossOrigin = 'anonymous';

			vidDuration = await getVideoDuration(
				currentPageUrl,
				'hls',
				videoElm,
				resourcePool,
			);
		}

		if (vidDuration) {
			setDuration(vidDuration);
			setTrimTimes({ start: 0, end: vidDuration });
		}
	};

	const onTimeUpdate = (): void => {
		const currentTime = getVideoElElapsed(
			videoElRef.current,
		);

		if (currentTime) {
			setElapsed(currentTime);
		}

		if (currentTime > trimTimes.end && videoElRef.current) {
			setVideoElElapsed(videoElRef.current, trimTimes.end);
			setVideoElPlaying(videoElRef.current, false);
			setIsPlaying(false);
		}
	};

	const onEnded = (): void => {
		setIsPlaying(false);
	};

	const videoMutateDebounce = useRef<NodeJS.Timeout | null>(null);

	const trimTimeChange = (newTrimTimes: TrimTime) => {
		if (videoElRef.current) {
			const pauseVideo = () => {
				setIsPlaying(false);
				setVideoElPlaying(videoElRef.current, false);
			};

			if (videoMutateDebounce.current) {
				clearTimeout(videoMutateDebounce.current);
			}

			const { start: oldStart, end: oldEnd } = trimTimes;
			const { start: newStart, end: newEnd } = newTrimTimes;
			if (newStart !== oldStart && newEnd !== oldEnd) {
				if (!(elapsed <= newEnd && elapsed >= newStart)) {
					// playhead no longer inside box
					if (newStart > elapsed) {
						pauseVideo();
						setVideoEditorElElapsed(newStart);
					} else {
						pauseVideo();
						setVideoEditorElElapsed(newEnd);
					}
				}
			} else if (newStart !== oldStart) {
				// left handle moved
				pauseVideo();
				setVideoEditorElElapsed(newStart);
			} else if (newEnd !== oldEnd) {
				// right handle moved
				pauseVideo();
				setVideoEditorElElapsed(newEnd);
			}
			setTrimTimes(newTrimTimes);
		}
	};

	const confirmSave = (newTitle: string | null): void => {
		if (token && currentMedia) {
			if (newTitle && !checkFileName(newTitle, currentMedia.type === MEDIA_TYPES.HLS)) {
				return;
			}

			const vidDuration = getVideoElDuration(
				videoElRef.current,
			) ?? duration;

			// If IVS video and less than a second from the end,
			// round up to nearest half second.
			const endTime = isHlsVideo && duration - trimTimes.end < 1
				? Math.ceil(vidDuration * 2) / 2
				: trimTimes.end;
			const [start, end] = getTimecodesFromSeconds(trimTimes.start, endTime);
			setRenameOpen(false);

			if (currentMedia.original_id) {
				TrimSaveOver(token, currentMedia.id, start, end);
			} else {
				TrimSaveAs(token, currentMedia.id, start, end, newTitle);
			}

			showAlert({
				message: 'Save Successful',
				description: 'Your trimmed file was successfully saved and will be available shortly.',
				duration: 5000,
				type: 'neutral',
				onClick: () => {
					closeModal();
					dispatch(setActiveSubTab(
						TABS.MEDIA,
						isHlsVideo
							? MEDIA_TABS.RECORDINGS
							: MEDIA_TABS.FILES,
					));
					Alerts.hideAlert(undefined);
				},
			});
		} else {
			Alerts.showAlert({ message: 'Error trimming file.', duration: 2000, type: 'error' });
		}
	};

	const handleSaveAttempt = () => {
		if (trimTimes.end - trimTimes.start < 3) {
			Alerts.showAlert({
				message: 'File below minimum required length',
				description: 'Trimmed copies can not be less than 3 seconds.',
				duration: 3000,
				type: 'error',
			});
		} else if (currentMedia?.original_id) {
			confirmSave(null);
		} else {
			setRenameOpen(true);
		}
	};

	const title = (mediaItem: MediaItem): string => {
		if (mediaItem.custom_name) {
			return mediaItem.custom_name;
		} if (mediaItem.original_name) {
			return mediaItem.original_name;
		}
		return '';
	};

	const handleOpenMediaItem = (mediaItem: MediaItem) => {
		dispatch(
			setModalState({
				modalOpen: true,
				modalTemplate: MODAL_TEMPLATES.VIDEO_EDITOR,
				modalData: {
					mediaId: mediaItem.id,
					editorPage:
						mediaItem.num_pages > 1
							? VIDEO_EDITOR_PAGES.SELECT_MEDIA_PAGE
							: VIDEO_EDITOR_PAGES.TRIM_EDITOR,
				},
			}),
		);
	};

	const originalMediaItem = useMemo((): MediaItem | null => {
		const originalMediaItemId: number | null | undefined = media.find(
			({ id }) => id === mediaId,
		)?.original_id;

		if (originalMediaItemId) {
			const getOriginalMediaItem: MediaItem | undefined = media.find(
				({ id }) => id === originalMediaItemId,
			);

			if (getOriginalMediaItem) {
				return getOriginalMediaItem;
			}
		}

		return null;
	}, [media, mediaId]);

	const canTrimOriginal = useMemo((): boolean => (
		canTrimMediaItem(originalMediaItem)
	), [canTrimMediaItem, originalMediaItem]);

	const goToOriginal = (): void => {
		if (originalMediaItem) {
			handleOpenMediaItem(originalMediaItem);
			setIsPlaying(false);
			setElapsed(0);
			setVideoTimer(0);
			setTimerRunning(false);
		}
	};

	const channelLogo: string = useMemo(() => (
		getChannelLogoFromMeeting(meeting) || TellyChannelLogo
	), [meeting]);

	return (
		<>
			{currentEditorPage === VIDEO_EDITOR_PAGES.TRIM_EDITOR && currentMedia && currentPageUrl && (<>
				<div className="video-editor-wrapper background-darker">
					<header className="video-editor-header">
						<div className="header-left">
							<HeaderLeftControls
								channelLogo={channelLogo}
								title={title(currentMedia)}
								backButtonHandler={closeModal}
							/>
							<span className="header-badge">
								{currentMedia?.original_id ? 'copy' : 'original'}
							</span>
						</div>
						<div className="header-right">
							{canTrimOriginal && (
								<button
									onClick={goToOriginal}
									className="trim-button-original"
								>
									<Icon
										name={ICONS.ARROW_LEFT_CURVED}
										color={COLORS.LIGHT_GRAY}
										size={11}
									/>
									<span className="text-light-gray">Go to original</span>
								</button>
							)}
							<button
								onClick={closeModal}
								className="padding-button-wide background-lighter corner-round-massive margin-right-medium button-background-shading"
							>
								<span className="text-light-gray">Close</span>
							</button>
							<button
								onClick={handleSaveAttempt}
								className="padding-button-wide background-green corner-round-massive button-background-shading"
							>
								<span className="text-dark">Save{currentMedia?.original_id ? '' : ' Copy'}</span>
							</button>
						</div>
					</header>
					<div className="video-editor-body">
						<div id="video-editor-video-wrapper" ref={videoWrapperRef}>
							<div className="video-editor-video-container" style={{ ...videoWidthHeight }}>
								<MediaVideo
									src={currentPageUrl}
									isHlsVideo={isHlsVideo}
									ref={videoElRef}
									loop={false}
									crossOrigin={undefined}
									onLoadedData={onLoadedData}
									onTimeUpdate={onTimeUpdate}
									posterImage={posterImage}
									onEnded={onEnded}
									elapsed={elapsed}
								/>
							</div>
						</div>
					</div>
					<div className="video-editor-footer">
						<TrimTimeline
							totalSeconds={duration}
							elapsedSeconds={elapsed}
							thumbnails={[currentMedia.thumbnail_url]}
							trimTimes={trimTimes}
							trimTimeChange={trimTimeChange}
							changeElapsedSeconds={(newElapsed: number) => setVideoEditorElElapsed(newElapsed)
							}
							{...{
								isPlaying,
								handleSkipBack,
								handleRewind,
								handlePlay,
								handleFastForward,
								handleSkipForward,
							}}
						/>
					</div>
				</div>
				<RenameDialog
					cancel={() => setRenameOpen(false)}
					confirm={confirmSave}
					open={renameOpen}
					name={title(currentMedia)}
					originalId={currentMedia.original_id ?? currentMedia.id}
				/>
			</>)}
		</>
	);
}
